import api from '../api/axios';
import { UserProfile } from '../models/userProfile';
import { Organisation } from '../models/organisation';
import { StaffProfile } from '../models/staffProfile';
import { OrgConfig } from '../models/orgConfig';
import { Entitlements, ENTITLEMENTS_DEFAULT } from '../models/entitlements';
import { getSelectedOrgId } from './selectedOrg';
import { SupportDataService, isSupportSession, supportOrgToOrganisation } from './supportDataService';

export class UserDataService {
  static async fetchUserProfile(email: string): Promise<UserProfile> {
    const response = await api.get(`/security/user?email=${email}`);
    return response.data;
  }

  static async fetchChildWhitelist(userId: number): Promise<number[]> {
    const response = await api.get(`/security/child-whitelist?userId=${userId}`);
    return response.data.childIds;
  }

  /**
   * Fetch all organisations the authenticated user belongs to.
   * This endpoint uses bootstrap auth (no org header needed).
   */
  static async fetchOrganisations(): Promise<Organisation[]> {
    // A support session belongs to no organisation, so its "organisations"
    // are the ones with a live grant. Every caller that rehydrates the
    // selected org after a refresh goes through here, so it is handled once.
    if (isSupportSession()) {
      const support = await SupportDataService.fetchMyOrganisations();
      return support.organisations.map(supportOrgToOrganisation);
    }
    const response = await api.get('/security/user-organizations');
    return response.data.organisations || [];
  }

  /**
   * Fetch the authenticated user's role IDs for the currently selected organisation.
   * Reads this tab's organisation (services/selectedOrg) so it matches what the axios interceptor sends.
   * Falls back to flattening all roles if no org is selected (legacy single-org behaviour).
   */
  static async fetchUserRoles(): Promise<number[]> {
    const orgs = await UserDataService.fetchOrganisations();
    const selectedOrgId = getSelectedOrgId();
    if (selectedOrgId) {
      const org = orgs.find((o) => o.id === parseInt(selectedOrgId, 10));
      return org ? org.roleIds : [];
    }
    // Fallback: flatten all roles (single-org / legacy behaviour)
    return orgs.flatMap((o) => o.roleIds);
  }

  /**
   * Fetch all available roles from the database.
   * Returns a mapping of role name → role ID, e.g. { Admin: 1, Editor: 2, ... }.
   */
  static async fetchRolesMap(): Promise<Record<string, number>> {
    const response = await api.get('/security/roles');
    const roles: { id: number; name: string }[] = response.data.roles || [];
    const map: Record<string, number> = {};
    for (const role of roles) {
      map[role.name] = role.id;
    }
    return map;
  }

  /** Fetch current organisation settings (name, email, logoUrl, inactivityTimeoutSeconds). */
  static async fetchOrgSettings(): Promise<{ id: number; name: string; email: string | null; logoUrl: string | null; brandColour: string | null; inactivityTimeoutSeconds: number }> {
    const response = await api.get('/security/organisation');
    return response.data;
  }

  /** Update organisation settings. Pass only the fields you want to change. */
  static async updateOrgSettings(payload: { name?: string; email?: string | null; logoS3Key?: string | null; brandColourCode?: string | null; inactivityTimeoutSeconds?: number }): Promise<void> {
    await api.put('/security/organisation', payload);
  }

  /**
   * Per-org feature config — schedule grid + attendance defaults + report
   * "Setting" label. Backed by `data.organisation_config` (migration 014).
   * Kept on a separate endpoint from /security/organisation so identity
   * concerns and feature config don't share an audit trail.
   */
  static async fetchOrgConfig(): Promise<OrgConfig> {
    const response = await api.get('/security/organisation/config');
    return response.data;
  }

  /** Update org config. Pass only the fields you want to change. */
  static async updateOrgConfig(payload: Partial<OrgConfig>): Promise<void> {
    await api.put('/security/organisation/config', payload);
  }

  /**
   * What the current organisation's plan includes (migration 047). Read-only,
   * and on its own endpoint rather than folded into org config — that payload
   * round-trips through a PUT the org's own Admin can call, and an
   * editable-looking entitlement field invites being wired up by accident.
   */
  static async fetchEntitlements(): Promise<Entitlements> {
    const response = await api.get('/security/organisation/entitlements');
    return { ...ENTITLEMENTS_DEFAULT, ...(response.data ?? {}) };
  }

  /**
   * Fetch staff profiles for the current organisation.
   * @param opts.pickerOnly   When true, restricts to users eligible for the
   *   shared-device switch-user grid: the requester themself (always),
   *   plus — when the requester is internal — other non-external Editor
   *   users. External requesters get only themself. Use for the
   *   StaffSelect screen.
   * @param opts.editorsOnly  When true, restricts to active users holding the
   *   Editor role (they may hold other roles too). Use for the schedule's
   *   teacher side-filter and session-editor dropdown. Leave both false for
   *   general staff listings (Dashboard names, etc.).
   *
   * Back-compat: also accepts a bare boolean as the legacy `pickerOnly` arg.
   */
  static async fetchStaffProfiles(
    opts: boolean | { pickerOnly?: boolean; editorsOnly?: boolean } = {}
  ): Promise<StaffProfile[]> {
    const { pickerOnly = false, editorsOnly = false } =
      typeof opts === 'boolean' ? { pickerOnly: opts } : opts;
    const params: Record<string, boolean> = {};
    if (pickerOnly) params.pickerOnly = true;
    if (editorsOnly) params.editorsOnly = true;
    const response = await api.get('/security/staff-profiles', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data.staff || [];
  }

  /**
   * Admin: fetch all staff in the current org including their role IDs.
   * @param includeInactive  When true, also returns disabled users so the
   *                         admin grid can surface and re-enable them.
   */
  static async fetchStaffWithRoles(includeInactive = false): Promise<StaffProfile[]> {
    const response = await api.get('/security/staff-with-roles', {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
    return response.data.staff || [];
  }

  /** Admin: replace a user's roles in the current organisation. */
  static async setUserRoles(userId: number, roleIds: number[]): Promise<void> {
    await api.put('/security/user-roles', { userId, roleIds });
  }

  /**
   * Admin: update a user's mutable fields. Email is intentionally NOT here —
   * it's bound to the Cognito identity and changing it is a separate flow.
   * isExternal flips the membership-level scoping flag.
   */
  static async updateUser(
    userId: number,
    payload: { name?: string; isActive?: boolean; isExternal?: boolean; colourCode?: string | null }
  ): Promise<void> {
    await api.put(`/security/user/${userId}`, payload);
  }

  /**
   * Admin: add a user to this organisation. Backend provisions a Cognito user
   * (force-reset on first sign-in) and an org membership row (with is_external
   * flag) plus the initial roles and child whitelist.
   *
   * The Cognito pool spans every organisation, so the email may already belong
   * to someone — a parent or bank staff member at another setting. `outcome`
   * says which of the three things happened, because only 'created' results in
   * an invitation email; the other two reuse the person's existing sign-in:
   *   created     — new identity, invitation email sent by Cognito
   *   linked      — existing identity gained a membership of this org
   *   reactivated — a previously disabled membership was switched back on
   * `name` is echoed back because a linked user keeps their existing name
   * (it's global, not per-org) rather than taking whatever was typed here.
   *
   * For 'linked'/'reactivated' the backend emails the person to say they've been
   * added; `notified` is false if that send failed. The membership is committed
   * either way, so this is a warning to pass on, not a failure.
   *
   * `invitationResent` is true when the existing account had never been signed
   * in to: instead of the "added" email it was sent a fresh invitation with a
   * new temporary password.
   */
  static async createUser(payload: {
    email: string;
    name: string;
    isExternal: boolean;
    roleIds: number[];
    childWhitelist: number[];
    colourCode?: string | null;
  }): Promise<{
    id: number;
    name: string;
    outcome: 'created' | 'linked' | 'reactivated';
    notified: boolean;
    invitationResent?: boolean;
  }> {
    const response = await api.post('/security/user', payload);
    return response.data;
  }

  /**
   * Admin: replace another user's child whitelist for the current organisation.
   * Empty array ⇒ no per-user filter applies (internal users see all children;
   * External users see nothing).
   */
  static async setUserChildWhitelist(userId: number, childIds: number[]): Promise<void> {
    await api.put(`/security/child-whitelist/${userId}`, { childIds });
  }

  /**
   * Admin: re-send the Cognito invitation email for a user still in
   * FORCE_CHANGE_PASSWORD state. Cognito generates a fresh temp password and
   * resets the 7-day expiry window. Throws with a 409 if the user is already
   * confirmed.
   */
  static async resendInvite(userId: number): Promise<void> {
    await api.post(`/security/user/${userId}/resend-invite`);
  }

  /**
   * Verify a staff member's PIN.
   * Returns the active user token and profile on success, throws on failure.
   */
  static async verifyPin(userId: number, pin: string): Promise<{ activeUserToken: string; activeUser: StaffProfile }> {
    const response = await api.post('/security/verify-pin', { userId, pin });
    return response.data;
  }

  /**
   * Admin-initiated PIN reset. Nulls the target user's PIN; they will be forced
   * through full Cognito re-auth + first-time PIN setup on their next sign-in.
   */
  static async resetUserPin(userId: number): Promise<void> {
    await api.post('/security/reset-user-pin', { userId });
  }

  /**
   * Self-service "Forget PIN" from the keypad. Nulls the staff member's PIN.
   * Caller is responsible for signing out of Cognito immediately afterwards.
   */
  static async forgetMyPin(userId: number): Promise<void> {
    await api.post('/security/forget-my-pin', { userId });
  }

  /** Check whether the currently authenticated Cognito user has a PIN set. */
  static async checkPinSet(): Promise<{ hasPin: boolean }> {
    const response = await api.get('/security/check-pin-set');
    return response.data;
  }

  /** Self-service: set the current user's own PIN for the first time. */
  static async setupMyPin(pin: string): Promise<void> {
    await api.post('/security/setup-my-pin', { pin });
  }

  /**
   * Create an active-user JWT for the currently signed-in Cognito user without a PIN.
   * Used on initial login so the user is auto-set as the active user.
   */
  static async loginAsSelf(): Promise<{ activeUserToken: string; activeUser: StaffProfile }> {
    const response = await api.post('/security/login-as-self');
    return response.data;
  }
}
